import { useEffect, useState } from 'react'
import { fetchShareAsObjectUrl, isUploadedShareUrl, toObjectUrl } from '../services/share-upload'
import { localCopyOf } from '../services/local-share-copy'

/**
 * Turn a shared media URL into one a `<video>` or `<iframe>` can actually load.
 *
 * Public URLs (a pasted link, an S3 presigned URL, a YouTube embed) pass
 * straight through. A backend attachment URL does not: it is behind bearer
 * auth, and a browser never sends the Authorization header on a subresource
 * request, so the element would 401. Those are fetched here — under this
 * client's own identity — and handed back as a local object URL.
 *
 * Every participant runs this independently, which is what makes an uploaded
 * file visible to the room without the file being made public to do it.
 */
export function usePlayableUrl(
  url: string | undefined | null,
  /** The share's display name. Only used for its extension, to correct a type
   *  the store guessed wrong — a CSV uploaded from a machine where Excel owns
   *  `.csv` arrives as an Excel type and is stored as an opaque download. */
  filename?: string | null,
): {
  playableUrl: string
  /** Content type of the fetched file, '' for a pass-through URL. */
  mime: string
  loading: boolean
  error: string | null
} {
  const [resolved, setResolved] = useState('')
  const [mime, setMime] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!url) {
      setResolved('')
      setMime('')
      setError(null)
      setLoading(false)
      return
    }

    // Anything WE uploaded is fetched, auth or no auth. A public bucket object
    // could be framed directly, but the store stamps `Content-Disposition:
    // attachment` on everything outside its inline-safe list, so framing one
    // downloads the file on every participant's machine and shows a blank
    // frame. Only a genuine outside link — a web page, a video embed — passes
    // through untouched.
    if (!isUploadedShareUrl(url)) {
      setResolved(url)
      setMime('')
      setError(null)
      setLoading(false)
      return
    }

    // This client uploaded it: show the file it already holds instead of
    // downloading it back, which made the presenter wait longest for their own
    // document. See local-share-copy.ts.
    const local = localCopyOf(url)
    if (local) {
      const next = toObjectUrl(local, filename)
      setResolved(next.objectUrl)
      setMime(next.mime)
      setError(null)
      setLoading(false)
      return () => URL.revokeObjectURL(next.objectUrl)
    }

    const controller = new AbortController()
    let objectUrl: string | null = null
    setLoading(true)
    setError(null)

    fetchShareAsObjectUrl(url, controller.signal, filename)
      .then((next) => {
        objectUrl = next.objectUrl
        setResolved(next.objectUrl)
        // The blob carries the served Content-Type, which is what decides
        // whether an element can render it at all.
        setMime(next.mime)
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        // Deliberately NOT falling back to the raw URL. That was the previous
        // behaviour and it is the bug: the store stamps
        // `Content-Disposition: attachment` on anything outside its inline-safe
        // list, so handing an iframe the store URL saves the file on every
        // participant's machine and leaves a blank frame. A share that cannot
        // be fetched has to say so and offer the download as a choice, rather
        // than performing one nobody asked for.
        const reason = err instanceof Error ? err.message : String(err)
        console.warn('[share] could not fetch the shared file:', reason, url)
        setError(reason || 'Could not load the shared file.')
        setResolved('')
        setMime('')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => {
      controller.abort()
      // Revoked on unmount / URL change — without this every re-share of a
      // 25 MB file would strand another copy in memory for the tab's lifetime.
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url, filename])

  return { playableUrl: resolved, mime, loading, error }
}
